import { RecommenderStep } from '../components/Recommender/types';
import type { Result } from '../types/result';
import { useCallback, useEffect, useState } from 'react';
import useFlowTrackingEvents from './useFlowTrackingEvents';
import { EventingLocation } from '../types/enums';
import { useDynamicExperienceContext } from '../providers/DynamicExperienceProvider';

type UseRecommendationPopup = ({
  recommendedResults,
  spotlightResults,
  productClickedCorrelationId,
}: {
  recommendedResults: Result[];
  spotlightResults: Result[];
  productClickedCorrelationId: string;
}) => {
  showPopupExperience: boolean;
  closePopupExperience: () => void;
  showSpotlight: boolean;
  showRecommender: boolean;
  popupExperienceStep: RecommenderStep;
  goToPopupProgramSelect: () => void;
  gotToPopupPiiConfirmation: () => void;
  goToPopupAdditionalQuestions: () => void;
  goToPopupExitStrategy: () => void;
  goToPopupSubmission: () => void;
};

const useRecommendationPopup: UseRecommendationPopup = ({
  recommendedResults,
  spotlightResults,
  productClickedCorrelationId,
}) => {
  const [step, setStep] = useState<RecommenderStep>(
    RecommenderStep.PROGRAM_SELECT,
  );
  const { triggerElementClickedEvent } = useFlowTrackingEvents();
  const { eventContext } = useDynamicExperienceContext();

  const showSpotlight = spotlightResults?.length > 0;

  const showRecommender = !showSpotlight && recommendedResults?.length > 0;

  const [showPopupExperience, setShowPopupExperience] = useState(false);

  const closePopupExperience = useCallback(() => {
    setShowPopupExperience(false);
    triggerElementClickedEvent({
      ...eventContext,
      productClickedCorrelationId,
      elementType: 'dexp-popup-close',
      name: 'dexp-popup-close-button',
      text: 'X',
      location: EventingLocation.POPUP,
    });
  }, [productClickedCorrelationId, eventContext, triggerElementClickedEvent]);

  const goToPopupProgramSelect = useCallback(
    () => setStep(RecommenderStep.PROGRAM_SELECT),
    [],
  );
  const gotToPopupPiiConfirmation = useCallback(
    () => setStep(RecommenderStep.PII_CONFIRMATION),
    [],
  );
  const goToPopupAdditionalQuestions = useCallback(
    () => setStep(RecommenderStep.ADDITIONAL_QUESTIONS),
    [],
  );
  const goToPopupExitStrategy = useCallback(
    () => setStep(RecommenderStep.EXIT_STRATEGY),
    [],
  );
  const goToPopupSubmission = useCallback(
    () => setStep(RecommenderStep.SUBMISSION),
    [],
  );

  useEffect(() => {
    setShowPopupExperience(
      spotlightResults.length > 0 || recommendedResults.length > 0,
    );
  }, [spotlightResults, recommendedResults]);
  return {
    showPopupExperience,
    closePopupExperience,
    showSpotlight,
    showRecommender,
    popupExperienceStep: step,
    goToPopupProgramSelect,
    gotToPopupPiiConfirmation,
    goToPopupAdditionalQuestions,
    goToPopupExitStrategy,
    goToPopupSubmission,
  };
};

export default useRecommendationPopup;
